import { environment } from '@energy-stacks/feature-config';
import { createBaseQuery } from '@energy-stacks/shared';
import { createApi } from '@reduxjs/toolkit/query/react';
import { vehiclesNormalizer } from './vehiclesNormalizer';
import { VehicleDto } from './vehicleDto';
import {
  CoreVehicleBrandDto,
  CoreVehicleDto,
  VehicleDetailsModel,
  vehicleDetailsNormalizer,
} from '@energy-stacks/fleet/feature-vehicles-data';
import { AddVehiclePayload } from './addVehiclePayload';
import { VehicleModel } from './vehicleModel';
import { EditVehicleFormData } from './editVehicleFormData';
import { editVehicleToPayload } from './editVehicleToPayload';

type VehicleBrandImage = {
  id: string;
  image: string;
};

export interface GetVehiclesRequestBody {
  dateFrom?: string;
  dateTo?: string;
}

type EditVehicleData = {
  formData: EditVehicleFormData;
  coreVehicleId: string;
  vehicleStatus: VehicleDetailsModel['status'];
  vehicleIdentificationNumber: VehicleDetailsModel['vehicleId'];
};

export const vehiclesApi = createApi({
  reducerPath: 'vehicles',
  keepUnusedDataFor: 0,
  tagTypes: [
    'Vehicles',
    'VehicleDetails',
    'CoreVehicles',
    'CoreVehicleBrands',
    'VehicleBrandImages',
  ],
  baseQuery: createBaseQuery(`${environment.ocppServiceUrl}/vehicles`),
  endpoints: (builder) => ({
    getVehicles: builder.query<VehicleModel[], void>({
      query: () => '/',
      providesTags: ['Vehicles'],
      transformResponse: vehiclesNormalizer,
    }),
    getVehicleDetails: builder.query<VehicleDetailsModel, string>({
      query: (vehicleId) => `/${vehicleId}`,
      transformResponse: vehicleDetailsNormalizer,
      providesTags: (result) => [
        {
          type: 'VehicleDetails',
          id: result?.vehicleId,
        },
      ],
    }),
    createVehicle: builder.mutation<VehicleDto, AddVehiclePayload>({
      query: (body) => ({
        url: '/',
        method: 'POST',
        body: body,
        params: {
          vehicleIdentificationNumber: body.vehicleIdentificationNumber,
        },
      }),
      invalidatesTags: ['Vehicles'],
    }),
    deleteVehicle: builder.mutation<void, string>({
      query: (vehicleId) => ({
        url: `/${vehicleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Vehicles'],
    }),
    editVehicle: builder.mutation<void, EditVehicleData>({
      query: (body) => ({
        url: `/${body.vehicleIdentificationNumber}`,
        method: 'PUT',
        body: editVehicleToPayload(
          body.formData,
          body.coreVehicleId,
          body.vehicleStatus
        ),
      }),
      invalidatesTags: (_, error, arg) =>
        !error
          ? [
              { type: 'VehicleDetails', id: arg.vehicleIdentificationNumber },
              'Vehicles',
            ]
          : [],
    }),
    getCoreVehicles: builder.query<CoreVehicleDto[], void>({
      query: () => '/core',
      providesTags: ['CoreVehicles'],
    }),
    getCoreVehicleBrands: builder.query<CoreVehicleBrandDto[], void>({
      query: () => '/core/vehicle-brands',
      providesTags: ['CoreVehicleBrands'],
    }),
    getVehicleBrandImages: builder.query<
      VehicleBrandImage[],
      CoreVehicleBrandDto['uuid'][]
    >({
      keepUnusedDataFor: 120,
      async queryFn(brandIds, _queryApi, _extraOptions, fetchWithBQ) {
        const logos = [];
        let error;

        for (const brandId of brandIds) {
          const response = await fetchWithBQ({
            url: `/core/vehicle-brands/${brandId}/image`,
            responseHandler: async (response) => {
              const blob = await response.blob();
              return new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const base64String = reader.result as string;
                  resolve(base64String);
                };
                reader.onerror = () => {
                  reject(new Error('Failed to read image file.'));
                };
                reader.readAsDataURL(blob);
              });
            },
          });
          if (response.error) {
            error = response.error;
          } else {
            logos.push({
              id: brandId,
              image: response.data,
            } as VehicleBrandImage);
          }
        }
        if (logos.length > 0) {
          return { data: logos };
        }
        return error ? { error: error } : { data: logos };
      },
      providesTags: (result) => {
        return (
          result?.map((brand) => ({
            type: 'VehicleBrandImages',
            id: brand.id,
          })) ?? []
        );
      },
    }),
  }),
});

export const {
  useGetVehiclesQuery,
  useGetVehicleDetailsQuery,
  useCreateVehicleMutation,
  useDeleteVehicleMutation,
  useEditVehicleMutation,
  useGetCoreVehiclesQuery,
  useGetCoreVehicleBrandsQuery,
  useGetVehicleBrandImagesQuery,
} = vehiclesApi;
